const { getAxiosInstance } = require('../connection');

module.exports = {
    /**
     * Queue a batch email send to one or more participants.
     *
     * Provide `templateId` OR both `subject` and `body`. Supply `participantIds`, `userIds`, or both.
     * Use a unique `guid` per send — resubmitting the same guid returns a 409 to prevent duplicate sends.
     *
     * @param {number} eventId
     * @param {{
     *   guid: string,
     *   participantIds?: number[],
     *   userIds?: number[],
     *   templateId?: number,
     *   subject?: string,
     *   body?: string,
     *   fromEmail?: string,
     *   fromName?: string,
     *   language?: string,
     *   includeInviteCodes?: boolean
     * }} data
     * @returns {Promise<{ batch_id: number, status: string, total_count: number }>}
     */
    sendEmail: (eventId, data) => {
        const { guid, participantIds, userIds, templateId, subject, body, fromEmail, fromName, language, includeInviteCodes } = data;

        return getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/participants/actions/send-email`,
            method: 'POST',
            data: {
                guid,
                participant_ids: participantIds,
                user_ids: userIds,
                template_id: templateId ?? undefined,
                subject: subject || undefined,
                body: body || undefined,
                from_email: fromEmail || undefined,
                from_name: fromName || undefined,
                language: language || undefined,
                include_invite_codes: includeInviteCodes ?? false,
            },
        }).then((r) => r.data);
    },

    /**
     * Send a test email to a single address without queuing a batch.
     *
     * @param {number} eventId
     * @param {{
     *   toEmail: string,
     *   subject: string,
     *   body: string,
     *   fromEmail?: string,
     *   fromName?: string,
     *   templateName?: string,
     *   language?: string,
     *   includeInviteCodes?: boolean
     * }} data
     * @returns {Promise<{ status: string }>}
     */
    sendTestEmail: (eventId, data) => {
        const { toEmail, subject, body, fromEmail, fromName, templateName, language, includeInviteCodes } = data;

        return getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/participants/actions/send-test-email`,
            method: 'POST',
            data: {
                to_email: toEmail,
                subject,
                body,
                from_email: fromEmail || undefined,
                from_name: fromName || undefined,
                template_name: templateName || undefined,
                language: language || undefined,
                include_invite_codes: includeInviteCodes ?? undefined,
            },
        }).then((r) => r.data);
    },

    /**
     * Generate a personal invite code for a single person.
     *
     * Optionally sends an invite email and/or adds them to the participant list.
     *
     * @param {number} eventId
     * @param {{
     *   toEmail: string,
     *   subject: string,
     *   body: string,
     *   firstName?: string,
     *   lastName?: string,
     *   fromEmail?: string,
     *   fromName?: string,
     *   templateName?: string,
     *   language?: string,
     *   sendEmail?: boolean,
     *   createParticipant?: boolean
     * }} data
     * @returns {Promise<{ code: string, email_sent: boolean, participant_created: boolean }>}
     */
    sendIndividualInvite: (eventId, data) => {
        const { toEmail, subject, body, firstName, lastName, fromEmail, fromName, templateName, language, sendEmail, createParticipant } = data;

        return getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/participants/actions/send-individual-invite`,
            method: 'POST',
            data: {
                subject,
                body,
                to_email: toEmail,
                first_name: firstName || undefined,
                last_name: lastName || undefined,
                from_email: fromEmail || undefined,
                from_name: fromName || undefined,
                template_name: templateName || undefined,
                language: language || undefined,
                send_email: sendEmail,
                create_participant: createParticipant,
            },
        }).then((r) => r.data);
    },

    /**
     * List email batches for an event.
     *
     * @param {number} eventId
     * @param {{ page?: number, per_page?: number }} [params]
     * @returns {Promise<{ batches: Array<{ id: number, guid: string, status: string, total_count: number, created_at: string, completed_at: string|null }>, total: number, page: number }>}
     */
    getBatches: (eventId, params) =>
        getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/email/batches`,
            method: 'GET',
            params,
        }).then((r) => r.data),

    /**
     * Get the status and delivery counts for a single email batch.
     *
     * @param {number} eventId
     * @param {number} batchId
     * @returns {Promise<{ id: number, guid: string, status: string, total_count: number, delivery_counts: { total: number, pending?: number, send_error?: number, delivered?: number, no_feedback?: number }, created_at: string, completed_at: string|null }>}
     */
    getBatch: (eventId, batchId) =>
        getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/email/batches/${encodeURIComponent(batchId)}`,
            method: 'GET',
        }).then((r) => r.data),

    /**
     * Retry a failed or stuck email batch.
     *
     * @param {number} eventId
     * @param {number} batchId
     * @returns {Promise<{ batch_id: number, status: string }>}
     */
    retryBatch: (eventId, batchId) =>
        getAxiosInstance({
            url: `/events/${encodeURIComponent(eventId)}/email/batches/${encodeURIComponent(batchId)}/retry`,
            method: 'POST',
        }).then((r) => r.data),
};
